import React, {
useEffect,
useState
} from "react";

import Swal from "sweetalert2";

import {
Button,
Dialog,
DialogActions,
DialogContent,
DialogTitle,
Grid,
TextField,
Autocomplete,
CircularProgress
} from "@mui/material";

import SchoolIcon from "@mui/icons-material/School";

import api from "../../api/api";

const AddAndUpDateGrades = ({


token,
userType,
gradeDistributions,
gradeClasses,
subjects,
periods,
selectedYear,

page,
rowsPerPage,
searchTerm,

fetchGradeDistributions,

setAlertMessage,
setAlertSeverity,
setSnackbarOpen,

editGradeId,
setEditGradeId


}) => {


const [selectedGradeClass, setSelectedGradeClass] =
    useState("");

const [selectedStudent, setSelectedStudent] =
    useState("");

const [selectedSubject, setSelectedSubject] =
    useState("");

const [selectedPeriod, setSelectedPeriod] =
    useState("");


const [filteredStudents, setFilteredStudents] =
    useState([]);


const [quizScore, setQuizScore] =
    useState(0);

const [assignmentScore, setAssignmentScore] =
    useState(0);

const [participationScore, setParticipationScore] =
    useState(0);

const [testScore, setTestScore] =
    useState(0);

const [scaleGrade, setScaleGrade] =
    useState(0);


const [allowTeacherUpdate, setAllowTeacherUpdate] =
    useState(false);


const [editingId, setEditingId] =
    useState(null);

const [editMode, setEditMode] =
    useState(false);

const [dialogOpen, setDialogOpen] =
    useState(false);

const [loadingStudents, setLoadingStudents] =
    useState(false);

const [submitting, setSubmitting] =
    useState(false);


/*
============================================================
EXTRACT THE EXACT BACKEND ERROR MESSAGE
============================================================
*/

const getBackendErrorMessage = (error) => {

    const responseData =
        error?.response?.data;


    /*
    NETWORK ERROR
    */

    if (!responseData) {

        if (error?.message === "Network Error") {

            return (
                "Unable to connect to the server. "
                + "Please check your internet connection "
                + "or confirm that the backend server is running."
            );

        }

        return (
            error?.message
            || "An unexpected error occurred."
        );

    }


    /*
    SIMPLE BACKEND ERROR

    {
        "error": "Message"
    }
    */

    if (
        typeof responseData.error ===
        "string"
    ) {

        return responseData.error;

    }


    /*
    SIMPLE DETAIL ERROR

    {
        "detail": "Message"
    }
    */

    if (
        typeof responseData.detail ===
        "string"
    ) {

        return responseData.detail;

    }


    /*
    DETAIL ARRAY

    {
        "detail": [
            "Message 1",
            "Message 2"
        ]
    }
    */

    if (
        Array.isArray(
            responseData.detail
        )
    ) {

        return responseData.detail
            .map(message => String(message))
            .join("\n");

    }


    /*
    NON-FIELD ERROR ARRAY
    */

    if (
        Array.isArray(responseData)
    ) {

        return responseData
            .map(message => String(message))
            .join("\n");

    }


    /*
    DJANGO REST FRAMEWORK SERIALIZER ERRORS

    {
        "student": [
            "This field is required."
        ],

        "subject": [
            "Invalid subject."
        ]
    }
    */

    if (
        typeof responseData ===
        "object"
    ) {

        const messages = [];


        Object.entries(
            responseData
        ).forEach(
            ([field, errors]) => {


                if (
                    Array.isArray(errors)
                ) {

                    messages.push(
                        `${field}: ${errors.join(", ")}`
                    );

                }

                else if (
                    typeof errors ===
                    "object"
                ) {

                    messages.push(
                        `${field}: ${JSON.stringify(errors)}`
                    );

                }

                else {

                    messages.push(
                        `${field}: ${errors}`
                    );

                }

            }
        );


        if (messages.length > 0) {

            return messages.join("\n");

        }

    }


    return (
        "The server returned an unknown error."
    );

};


/*
============================================================
SHOW ERROR
============================================================
*/

const showBackendError = (error) => {

    console.error(
        "Grade Distribution Error:",
        error?.response?.data || error
    );


    const message =
        getBackendErrorMessage(error);


    setAlertMessage(message);

    setAlertSeverity("error");

    setSnackbarOpen(true);


    Swal.fire({

        icon: "error",

        title: "Unable to Save Grade",

        text: message,

        confirmButtonText: "OK",

        confirmButtonColor: "#d33"

    });

};


/*
============================================================
LOAD STUDENTS FOR SELECTED CLASS
============================================================
*/

useEffect(() => {


    if (
        !selectedGradeClass
        || !selectedYear?.id
    ) {

        setFilteredStudents([]);

        return;

    }


    const loadStudents = async () => {

        try {

            setLoadingStudents(true);


            const response =
                await api.get(

                    `/students-in-class/${selectedGradeClass}/`,

                    {

                        headers: {

                            Authorization:
                                `Bearer ${token}`

                        },

                        params: {

                            academic_year:
                                selectedYear.id

                        }

                    }

                );


            setFilteredStudents(

                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []

            );


        }

        catch (error) {

            console.error(
                "Error loading students:",
                error?.response?.data || error
            );


            setFilteredStudents([]);


            const message =
                getBackendErrorMessage(error);


            setAlertMessage(message);

            setAlertSeverity("error");

            setSnackbarOpen(true);

        }

        finally {

            setLoadingStudents(false);

        }

    };


    loadStudents();


}, [

    selectedGradeClass,
    selectedYear,
    token

]);


/*
============================================================
OPEN DIALOG
============================================================
*/

const handleOpenDialog = async (
    id = null
) => {


    if (id) {


        const item =
            gradeDistributions.find(
                grade => grade.id === id
            );


        if (!item) {

            Swal.fire({

                icon: "error",

                title: "Grade Not Found",

                text:
                    "The selected grade could not be found."

            });

            return;

        }


        setEditingId(id);

        setEditMode(true);


        setSelectedGradeClass(
            item.grade_class
        );


        setSelectedStudent(
            item.student
        );


        setSelectedSubject(
            item.subject
        );


        setSelectedPeriod(
            item.period
        );


        setQuizScore(
            item.quiz_score ?? 0
        );


        setAssignmentScore(
            item.assignment_score ?? 0
        );


        setParticipationScore(
            item.participation_score ?? 0
        );


        setTestScore(
            item.test_score ?? 0
        );


        setScaleGrade(
            item.scale_grade ?? 0
        );


        setAllowTeacherUpdate(
            Boolean(
                item.allow_teacher_update
            )
        );

    }


    else {


        setEditingId(null);

        setEditMode(false);


        setSelectedGradeClass("");

        setSelectedStudent("");

        setSelectedSubject("");

        setSelectedPeriod("");


        setQuizScore(0);

        setAssignmentScore(0);

        setParticipationScore(0);

        setTestScore(0);

        setScaleGrade(0);


        setAllowTeacherUpdate(false);


        setFilteredStudents([]);

    }


    setDialogOpen(true);

};


/*
============================================================
AUTOMATICALLY OPEN EDIT DIALOG
============================================================
*/

useEffect(() => {


    if (editGradeId) {

        handleOpenDialog(
            editGradeId
        );

    }


}, [editGradeId]);


/*
============================================================
CLOSE DIALOG
============================================================
*/

const handleCloseDialog = () => {


    if (submitting) {

        return;

    }


    setDialogOpen(false);

    setEditGradeId(null);

};


/*
============================================================
SUBMIT GRADE
============================================================
*/

const handleSubmit = async (
    event
) => {


    event.preventDefault();


    /*
    REQUIRED FIELDS
    */

    if (

        !selectedGradeClass
        || !selectedStudent
        || !selectedSubject
        || !selectedPeriod

    ) {


        const message =
            "Grade Class, Student, Subject, and Period are all required.";


        setAlertMessage(message);

        setAlertSeverity("error");

        setSnackbarOpen(true);


        Swal.fire({

            icon: "warning",

            title: "Missing Required Fields",

            text: message

        });


        return;

    }


    /*
    TEACHER UPDATE PERMISSION
    */

    if (

        userType === "teacher"
        && editMode
        && !allowTeacherUpdate

    ) {


        const message =
            "You cannot update this grade. Admin, VPI, or VPA must enable teacher update permission first.";


        setAlertMessage(message);

        setAlertSeverity("error");

        setSnackbarOpen(true);


        Swal.fire({

            icon: "error",

            title: "Permission Denied",

            text: message

        });


        return;

    }


    /*
    REQUEST DATA
    */

    const data = {

        grade_class:
            selectedGradeClass,

        student:
            selectedStudent,

        subject:
            selectedSubject,

        period:
            selectedPeriod,


        quiz_score:
            quizScore,

        assignment_score:
            assignmentScore,

        participation_score:
            participationScore,

        test_score:
            testScore,

        scale_grade:
            scaleGrade,


        allow_teacher_update:
            allowTeacherUpdate

    };


    try {


        setSubmitting(true);


        await api({

            method:
                editingId
                    ? "put"
                    : "post",


            url:

                editingId

                    ? `/api/users/grades-distribution/${editingId}/`

                    : "/api/users/grades-distribution/",


            data,


            headers: {

                Authorization:
                    `Bearer ${token}`

            }

        });


        const successMessage =
            editMode
                ? "Grade updated successfully."
                : "Grade created successfully.";


        setAlertMessage(
            successMessage
        );

        setAlertSeverity(
            "success"
        );

        setSnackbarOpen(
            true
        );


        Swal.fire({

            icon: "success",

            title: "Success",

            text: successMessage,

            timer: 1800,

            showConfirmButton: false

        });


        await fetchGradeDistributions(

            page + 1,

            rowsPerPage,

            searchTerm

        );


        setDialogOpen(false);

        setEditGradeId(null);


    }

    catch (error) {


        showBackendError(
            error
        );


    }

    finally {

        setSubmitting(false);

    }

};


/*
============================================================
RENDER
============================================================
*/

return (

    <>


        <Button

            variant="contained"

            startIcon={
                <SchoolIcon />
            }

            onClick={() =>
                handleOpenDialog()
            }

            disabled={
                submitting
            }

        >

            Add Grade

        </Button>


        <Dialog

            open={
                dialogOpen
            }

            onClose={
                handleCloseDialog
            }

            fullWidth

            maxWidth="sm"

        >


            <DialogTitle>

                {editMode
                    ? "Edit Grade"
                    : "Add Grade"}

            </DialogTitle>


            <DialogContent>


                <Grid

                    container

                    spacing={2}

                    sx={{
                        mt: 0.5
                    }}

                >


                    {/* GRADE CLASS */}

                    <Grid

                        item

                        xs={12}

                    >

                        <Autocomplete

                            options={
                                gradeClasses || []
                            }

                            getOptionLabel={
                                option =>
                                    option?.name || ""
                            }

                            value={

                                gradeClasses?.find(

                                    item =>
                                        item.id ===
                                        selectedGradeClass

                                ) || null

                            }

                            onChange={(

                                event,
                                value

                            ) => {


                                setSelectedGradeClass(

                                    value?.id || ""

                                );


                                setSelectedStudent("");

                                setFilteredStudents([]);

                            }}

                            isOptionEqualToValue={(

                                option,
                                value

                            ) => (

                                option.id ===
                                value.id

                            )}

                            renderInput={

                                params => (

                                    <TextField

                                        {...params}

                                        label="Grade Class"

                                        required

                                        fullWidth

                                    />

                                )

                            }

                        />

                    </Grid>


                    {/* STUDENT */}

                    <Grid

                        item

                        xs={12}

                    >

                        <Autocomplete

                            options={
                                filteredStudents
                            }

                            loading={
                                loadingStudents
                            }

                            getOptionLabel={

                                option =>
                                    option?.full_name
                                    || ""

                            }

                            value={

                                filteredStudents.find(

                                    student =>
                                        student.id ===
                                        selectedStudent

                                ) || null

                            }

                            onChange={(

                                event,
                                value

                            ) => {


                                setSelectedStudent(

                                    value?.id || ""

                                );

                            }}

                            isOptionEqualToValue={(

                                option,
                                value

                            ) => (

                                option.id ===
                                value.id

                            )}

                            renderInput={

                                params => (

                                    <TextField

                                        {...params}

                                        label="Student"

                                        required

                                        fullWidth

                                        InputProps={{

                                            ...params.InputProps,

                                            endAdornment: (

                                                <>

                                                    {

                                                        loadingStudents
                                                            ? (

                                                                <CircularProgress

                                                                    color="inherit"

                                                                    size={20}

                                                                />

                                                            )

                                                            : null

                                                    }

                                                    {

                                                        params.InputProps.endAdornment

                                                    }

                                                </>

                                            )

                                        }}

                                    />

                                )

                            }

                        />

                    </Grid>


                    {/* SUBJECT */}

                    <Grid

                        item

                        xs={12}

                    >

                        <Autocomplete

                            options={
                                subjects || []
                            }

                            getOptionLabel={

                                option =>
                                    option?.name
                                    || ""

                            }

                            value={

                                subjects?.find(

                                    subject =>
                                        subject.id ===
                                        selectedSubject

                                ) || null

                            }

                            onChange={(

                                event,
                                value

                            ) => {


                                setSelectedSubject(

                                    value?.id || ""

                                );

                            }}

                            isOptionEqualToValue={(

                                option,
                                value

                            ) => (

                                option.id ===
                                value.id

                            )}

                            renderInput={

                                params => (

                                    <TextField

                                        {...params}

                                        label="Subject"

                                        required

                                        fullWidth

                                    />

                                )

                            }

                        />

                    </Grid>


                    {/* PERIOD */}

                    <Grid

                        item

                        xs={12}

                    >

                        <Autocomplete

                            options={
                                periods || []
                            }

                            getOptionLabel={

                                option =>
                                    option?.name
                                    || ""

                            }

                            value={

                                periods?.find(

                                    period =>
                                        period.id ===
                                        selectedPeriod

                                ) || null

                            }

                            onChange={(

                                event,
                                value

                            ) => {


                                setSelectedPeriod(

                                    value?.id || ""

                                );

                            }}

                            isOptionEqualToValue={(

                                option,
                                value

                            ) => (

                                option.id ===
                                value.id

                            )}

                            renderInput={

                                params => (

                                    <TextField

                                        {...params}

                                        label="Period"

                                        required

                                        fullWidth

                                    />

                                )

                            }

                        />

                    </Grid>


                    {/* QUIZ SCORE */}

                    <Grid

                        item

                        xs={6}

                    >

                        <TextField

                            label="Quiz Score"

                            type="number"

                            value={
                                quizScore
                            }

                            onChange={

                                event =>
                                    setQuizScore(

                                        Number(
                                            event.target.value
                                        )

                                    )

                            }

                            fullWidth

                            inputProps={{
                                min: 0
                            }}

                        />

                    </Grid>


                    {/* ASSIGNMENT SCORE */}

                    <Grid

                        item

                        xs={6}

                    >

                        <TextField

                            label="Assignment Score"

                            type="number"

                            value={
                                assignmentScore
                            }

                            onChange={

                                event =>
                                    setAssignmentScore(

                                        Number(
                                            event.target.value
                                        )

                                    )

                            }

                            fullWidth

                            inputProps={{
                                min: 0
                            }}

                        />

                    </Grid>


                    {/* PARTICIPATION SCORE */}

                    <Grid

                        item

                        xs={6}

                    >

                        <TextField

                            label="Participation Score"

                            type="number"

                            value={
                                participationScore
                            }

                            onChange={

                                event =>
                                    setParticipationScore(

                                        Number(
                                            event.target.value
                                        )

                                    )

                            }

                            fullWidth

                            inputProps={{
                                min: 0
                            }}

                        />

                    </Grid>


                    {/* TEST SCORE */}

                    <Grid

                        item

                        xs={6}

                    >

                        <TextField

                            label="Test Score"

                            type="number"

                            value={
                                testScore
                            }

                            onChange={

                                event =>
                                    setTestScore(

                                        Number(
                                            event.target.value
                                        )

                                    )

                            }

                            fullWidth

                            inputProps={{
                                min: 0
                            }}

                        />

                    </Grid>


                    {/* SCALE GRADE */}

                    <Grid

                        item

                        xs={12}

                    >

                        <TextField

                            label="Scale Grade"

                            type="number"

                            value={
                                scaleGrade
                            }

                            onChange={

                                event =>
                                    setScaleGrade(

                                        Number(
                                            event.target.value
                                        )

                                    )

                            }

                            fullWidth

                            inputProps={{
                                min: 0
                            }}

                        />

                    </Grid>


                </Grid>


            </DialogContent>


            <DialogActions>


                <Button

                    onClick={
                        handleCloseDialog
                    }

                    disabled={
                        submitting
                    }

                >

                    Cancel

                </Button>


                <Button

                    variant="contained"

                    onClick={
                        handleSubmit
                    }

                    disabled={
                        submitting
                    }

                    startIcon={

                        submitting
                            ? (

                                <CircularProgress

                                    size={18}

                                    color="inherit"

                                />

                            )

                            : null

                    }

                >

                    {submitting
                        ? "Saving..."
                        : "Submit"}

                </Button>


            </DialogActions>


        </Dialog>


    </>

);


};

export default AddAndUpDateGrades;








// import React,{useState,useEffect} from "react";
// import Swal from "sweetalert2";

// import {
// Button,
// Dialog,
// DialogActions,
// DialogContent,
// DialogTitle,
// Grid,
// TextField,
// Autocomplete
// } from "@mui/material";

// import SchoolIcon from "@mui/icons-material/School";

// import api from "../../api/api";


// const AddAndUpDateGrades = ({

// token,
// userType,
// gradeDistributions,
// gradeClasses,
// subjects,
// periods,
// selectedYear,

// page,
// rowsPerPage,
// searchTerm,

// fetchGradeDistributions,

// setAlertMessage,
// setAlertSeverity,
// setSnackbarOpen,

// editGradeId,
// setEditGradeId

// })=>{


// const [selectedGradeClass,setSelectedGradeClass]=useState("");
// const [selectedStudent,setSelectedStudent]=useState("");
// const [selectedSubject,setSelectedSubject]=useState("");
// const [selectedPeriod,setSelectedPeriod]=useState("");

// const [filteredStudents,setFilteredStudents]=useState([]);


// const [quizScore,setQuizScore]=useState(0);
// const [assignmentScore,setAssignmentScore]=useState(0);
// const [participationScore,setParticipationScore]=useState(0);
// const [testScore,setTestScore]=useState(0);
// const [scaleGrade,setScaleGrade]=useState(0);

// const [allowTeacherUpdate,setAllowTeacherUpdate]=useState(false);


// const [editingId,setEditingId]=useState(null);
// const [editMode,setEditMode]=useState(false);

// const [dialogOpen,setDialogOpen]=useState(false);





// useEffect(()=>{


// if(editGradeId){

// handleOpenDialog(editGradeId);

// }


// },[editGradeId]);





// useEffect(()=>{


// if(selectedGradeClass && selectedYear){


// api.get(
// `/students-in-class/${selectedGradeClass}/`,
// {
// headers:{
// Authorization:`Bearer ${token}`
// },
// params:{
// academic_year:selectedYear.id
// }
// }
// )

// .then(res=>{

// setFilteredStudents(res.data);

// })

// .catch(()=>{

// setFilteredStudents([]);

// });


// }
// else{

// setFilteredStudents([]);

// }


// },[selectedGradeClass,selectedYear]);







// const handleOpenDialog=async(id=null)=>{


// if(id){


// const item=
// gradeDistributions.find(
// x=>x.id===id
// );



// setEditingId(id);
// setEditMode(true);


// setSelectedGradeClass(
// item.grade_class
// );



// try{


// const res=await api.get(

// `/students-in-class/${item.grade_class}/`,

// {
// headers:{
// Authorization:`Bearer ${token}`
// },

// params:{
// academic_year:selectedYear?.id
// }

// }

// );


// setFilteredStudents(res.data);


// }catch{

// setFilteredStudents([]);

// }




// setSelectedStudent(item.student);

// setSelectedSubject(item.subject);

// setSelectedPeriod(item.period);


// setQuizScore(item.quiz_score);

// setAssignmentScore(item.assignment_score);

// setParticipationScore(item.participation_score);

// setTestScore(item.test_score);

// setScaleGrade(
// item.scale_grade ?? 0
// );


// setAllowTeacherUpdate(
// item.allow_teacher_update
// );



// }

// else{


// setEditingId(null);

// setEditMode(false);


// setSelectedGradeClass("");

// setSelectedStudent("");

// setSelectedSubject("");

// setSelectedPeriod("");


// setQuizScore(0);

// setAssignmentScore(0);

// setParticipationScore(0);

// setTestScore(0);

// setScaleGrade(0);


// setAllowTeacherUpdate(false);


// setFilteredStudents([]);

// }



// setDialogOpen(true);


// };







// const handleSubmit=async(e)=>{


// e.preventDefault();



// if(
// !selectedGradeClass ||
// !selectedStudent ||
// !selectedSubject ||
// !selectedPeriod
// ){


// setAlertMessage(
// "All fields are required."
// );

// setAlertSeverity("error");

// setSnackbarOpen(true);

// return;


// }




// if(
// userType==="teacher" &&
// editMode &&
// !allowTeacherUpdate
// ){


// Swal.fire(
// "Permission Denied",
// "Cannot update without permission",
// "error"
// );


// return;


// }





// const data={


// grade_class:selectedGradeClass,

// student:selectedStudent,

// subject:selectedSubject,

// period:selectedPeriod,


// quiz_score:quizScore,

// assignment_score:assignmentScore,

// participation_score:participationScore,

// test_score:testScore,

// scale_grade:scaleGrade,

// allow_teacher_update:allowTeacherUpdate


// };




// try{


// await api({

// method:editingId?
// "put":
// "post",


// url:editingId
// ?
// `/api/users/grades-distribution/${editingId}/`
// :
// "/api/users/grades-distribution/",


// data,


// headers:{
// Authorization:`Bearer ${token}`
// }

// });



// setAlertMessage(
// editMode?
// "Updated successfully":
// "Created successfully"
// );


// setAlertSeverity("success");

// setSnackbarOpen(true);



// await fetchGradeDistributions(
// page+1,
// rowsPerPage,
// searchTerm
// );



// setDialogOpen(false);

// setEditGradeId(null);



// }

// catch(error){


// setAlertMessage(

// error.response?.data?.detail ||
// "Error saving grade."

// );


// setAlertSeverity("error");

// setSnackbarOpen(true);


// }


// };






// return(

// <>



// <Button
// variant="contained"
// startIcon={<SchoolIcon/>}
// onClick={()=>handleOpenDialog()}
// >
// Add Grade
// </Button>





// <Dialog

// open={dialogOpen}

// onClose={()=>{}}

// >



// <DialogTitle>

// {editMode?"Edit":"Add"} Grade

// </DialogTitle>



// <DialogContent>


// <Grid container spacing={2}>


// <Grid item xs={12}>

// <Autocomplete

// options={gradeClasses}

// getOptionLabel={
// o=>o.name
// }


// value={
// gradeClasses.find(
// x=>x.id===selectedGradeClass
// )||null
// }


// onChange={
// (e,v)=>
// setSelectedGradeClass(v?.id||"")
// }



// renderInput={
// p=>
// <TextField
// {...p}
// label="Grade Class"
// />
// }


// />


// </Grid>





// <Grid item xs={12}>

// <Autocomplete

// options={filteredStudents}

// getOptionLabel={
// o=>o.full_name
// }



// value={
// filteredStudents.find(
// x=>x.id===selectedStudent
// )||null
// }


// onChange={
// (e,v)=>
// setSelectedStudent(v?.id||"")
// }



// renderInput={
// p=>
// <TextField
// {...p}
// label="Student"
// />
// }


// />


// </Grid>





// <Grid item xs={12}>

// <Autocomplete

// options={subjects}

// getOptionLabel={
// o=>o.name
// }


// value={
// subjects.find(
// x=>x.id===selectedSubject
// )||null
// }



// onChange={
// (e,v)=>
// setSelectedSubject(v?.id||"")
// }



// renderInput={
// p=>
// <TextField
// {...p}
// label="Subject"
// />
// }


// />


// </Grid>






// <Grid item xs={12}>

// <Autocomplete

// options={periods}

// getOptionLabel={
// o=>o.name
// }


// value={
// periods.find(
// x=>x.id===selectedPeriod
// )||null
// }


// onChange={
// (e,v)=>
// setSelectedPeriod(v?.id||"")
// }



// renderInput={
// p=>
// <TextField
// {...p}
// label="Period"
// />
// }


// />


// </Grid>






// <Grid item xs={6}>

// <TextField

// label="Quiz Score"

// type="number"

// value={quizScore}

// onChange={
// e=>setQuizScore(Number(e.target.value))
// }

// fullWidth

// />

// </Grid>



// <Grid item xs={6}>

// <TextField

// label="Assignment Score"

// type="number"

// value={assignmentScore}

// onChange={
// e=>setAssignmentScore(Number(e.target.value))
// }

// fullWidth

// />

// </Grid>




// <Grid item xs={6}>

// <TextField

// label="Participation Score"

// type="number"

// value={participationScore}

// onChange={
// e=>setParticipationScore(Number(e.target.value))
// }

// fullWidth

// />

// </Grid>




// <Grid item xs={6}>

// <TextField

// label="Test Score"

// type="number"

// value={testScore}

// onChange={
// e=>setTestScore(Number(e.target.value))
// }

// fullWidth

// />

// </Grid>




// <Grid item xs={12}>

// <TextField

// label="Scale Grade"

// type="number"

// value={scaleGrade}

// onChange={
// e=>setScaleGrade(Number(e.target.value))
// }

// fullWidth

// />

// </Grid>



// </Grid>


// </DialogContent>





// <DialogActions>


// <Button

// onClick={()=>{

// setDialogOpen(false);

// setEditGradeId(null);

// }}

// >

// Cancel

// </Button>



// <Button

// variant="contained"

// onClick={handleSubmit}

// >

// Submit

// </Button>



// </DialogActions>




// </Dialog>



// </>

// );


// };


// export default AddAndUpDateGrades;